/* ===== events — delegated data-action handlers =====
 * v2 screens never use inline on-click attributes (CSP-ready). Instead every
 * interactive element carries `data-action="name"` and the screen binds
 * one listener per event type on its root:
 *
 *   delegate(root, {
 *     click:  { 'open-po': (e, el, data) => …, 'retry': () => … },
 *     input:  { 'qty': (e, el, data) => … },
 *     change: { 'supplier': … },
 *     keydown:{ 'qty': (e) => { if (e.key === 'Enter') … } },
 *     submit: { 'save': (e, el) => { e.preventDefault(); … } },
 *   }, ctx.signal);
 *
 * Handlers receive (event, actionElement, actionElement.dataset). The
 * listeners are bound with the screen's AbortSignal so unmount removes
 * them — nothing keeps firing after navigation.
 *
 * Pure module (no DOM access at import).
 */

const EVENT_OPTIONS = {
  // Form-control events do not bubble from every browser the same way;
  // capture keeps `input`/`change` delegation reliable inside shadowless DOM.
  focus: { capture: true },
  blur: { capture: true },
};

export function delegate(root, handlers, signal) {
  if (!root || !handlers) return () => {};
  const unbinders = [];
  for (const type of Object.keys(handlers)) {
    const map = handlers[type];
    if (!map || typeof map !== 'object') continue;
    const listener = (e) => {
      const target = e.target;
      const closest = target && typeof target.closest === 'function' ? target : target?.parentElement;
      const el = closest ? closest.closest('[data-action]') : null;
      if (!el || !root.contains(el)) return;
      // Disabled controls never fire click natively; guard delegated
      // ancestors (e.g. a disabled button inside a data-action row).
      if (type === 'click' && target.closest && target.closest('button:disabled, fieldset:disabled')) return;
      const action = el.dataset.action;
      const fn = map[action];
      if (typeof fn !== 'function') return;
      if (type === 'click' && el.tagName === 'A' && (el.getAttribute('href') || '#') === '#') e.preventDefault();
      fn(e, el, el.dataset);
    };
    const opts = { ...(EVENT_OPTIONS[type] || {}), signal };
    root.addEventListener(type, listener, opts);
    unbinders.push(() => root.removeEventListener(type, listener, opts));
  }
  return () => { for (const u of unbinders) u(); };
}

/** Space/Enter activate non-button elements that carry role="button". */
export function keyboardActivation(root, signal) {
  if (!root) return;
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = e.target;
    if (!el || el.tagName === 'BUTTON' || el.tagName === 'A' || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return;
    if (el.getAttribute && el.getAttribute('role') === 'button' && el.dataset && el.dataset.action) {
      e.preventDefault();
      el.click();
    }
  }, { signal });
}

/** Debounce helper for search boxes; timers die with the signal. */
export function debounce(fn, ms, signal) {
  let t = null;
  const wrapped = (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => { t = null; fn(...args); }, ms);
  };
  wrapped.cancel = () => { if (t) clearTimeout(t); t = null; };
  if (signal) signal.addEventListener('abort', wrapped.cancel, { once: true });
  return wrapped;
}
